/**
 * ColumnWeatherRow — `column.weather` renderer. Typed view over a
 * weather source row's config attributes (`lid`).
 *
 * Hydrated as a hidden child of any source with wire type `"weather"`
 * via the `WEATHER.defaultAttributes.childSpecs` entry in
 * `row-type-specs-builtin.ts`. Cells on the parent source carry the
 * weather data (shape probed by `WEATHER_FACADE`); this renderer is
 * a pure typed accessor over config.
 */

import type { Option } from "../../core/option.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import { RendererRow } from "./renderer-row.ts";
import { SourceRow } from "./source-row.ts";

/** The renderer row that shows its source row's weather readings. */
export class ColumnWeatherRow extends RendererRow {
  get source(): SourceRow {
    const p = this.parent;
    if (!(p instanceof SourceRow)) {
      throw new Error(
        `ColumnWeatherRow: parent of ${this.id.rawId} is not a SourceRow ` +
          `(got ${p?.constructor.name ?? "undefined"}).`,
      );
    }
    return p;
  }

  /**
   * The location this renderer is bound to (one of
   * `client.getWeatherLocations()`'s ids, `@tento/lona`): the one answer the
   * legend and the cell lookup share. It follows the binding — the location
   * the data address names (`[weather@lona.so]:~weather:{lid}`, read off
   * `dataAddress`), else the data row's `lid` attribute. `null`: neither
   * names one (unbound, or the default `:~lona:weather` address) — the row
   * shows no location.
   */
  get lid(): Option<number> {
    return ColumnWeatherRow.locationOfAddress(this.dataAddress) ??
      this.dataRow.attributesAs(ColumnWeatherRow.decodeAttrs)?.lid ?? null;
  }
}

/** Types and values that go with `ColumnWeatherRow`: `Attrs`, `decodeAttrs`. */
export namespace ColumnWeatherRow {
  export type Attrs = Partial<{
    lid: number;
  }>;

  /**
   * The location id a weather data address names: `:~weather:{lid}`, with or
   * without its `[owner]:` scope. `null` for any other address (including
   * the default `:~lona:weather`) or none.
   */
  export function locationOfAddress(address: Option<string>): Option<number> {
    if (address == null) return null;
    const unscoped = address.startsWith("[") && address.includes("]:") ? address.slice(address.indexOf("]:") + 1) : address;
    const match = /^:~weather:(\d+)$/.exec(unscoped);
    return match == null ? null : Number(match[1]);
  }

  /** Read a weather source's location id (`lid`) off its bag. */
  export function decodeAttrs(attributes: JsonObject): Attrs {
    const lid = Json.number(attributes, "lid");
    return lid != null ? { lid } : {};
  }
}
